import KpiService from '../services/kpi.service'

/**
 * Utilitaires pour le traitement des données KPI
 */

/**
 * Fusionne deux objets en profondeur
 */
export const deepMerge = (target, ...sources) => {
  const { keys } = Object
  const isObject = (a) => typeof a === 'object' && !Array.isArray(a)
  
  const merge = (a, b) =>
    isObject(a) && isObject(b)
      ? deepMerge(a, b)
      : isObject(a) && !isObject(b)
      ? a
      : b
      
  const coalesceByKey = (source) => (acc, key) =>
    (acc[key] && source[key]
      ? (acc[key] = merge(acc[key], source[key]))
      : (acc[key] = source[key])) && acc

  return sources.reduce(
    (acc, source) => keys(source).reduce(coalesceByKey(source), acc),
    target,
  )
}

/**
 * Somme plusieurs tableaux élément par élément
 */
export const sumArrays = (...arrays) => {
  const n = arrays.reduce((max, xs) => Math.max(max, xs.length), 0)
  const result = Array.from({ length: n })
  return result.map((_, i) =>
    arrays.map((xs) => xs[i] || 0).reduce((sum, x) => sum + x * 1, 0),
  )
}

/**
 * Appelle l'API KPI pour un code donné
 */
export const kpiCall = (code) => {
  return new Promise((resolve, reject) => {
    KpiService.getKpi(code)
      .then(async (res) => {
        // Test data pour l'environnement de test
        if (window.location.origin === "https://estoc-cybervision.intradef.gouv") {
          res = addTestData(res)
        }

        if (!res.data || !res.data[0] || !res.data[0].document) {
          return reject(res)
        }
        return resolve(res.data[0].document)
      })
      .catch((err) => console.log(err))
  })
}

/**
 * Ajoute des données de test pour l'environnement de développement
 */
const addTestData = (res) => {
  const arrayTest = [
    'Postes de travail, TPV',
    'Stations blanches',
    'Serveurs, autres',
    'lameserver',
    'query',
    'queryerror',
    'security',
  ]
  
  res.data[0].document = {
    perimetre_supervise: {
      result: {
        rows: [],
        fields: []
      }
    }
  }
  
  res.data[0].document.perimetre_supervise.result.rows.length = 0
  const randomLoop = Math.floor(Math.random() * 5)
  
  for (let i = 0; i < randomLoop; i++) {
    const randomNumber = Math.floor(Math.random() * arrayTest.length)
    const exist = res.data[0].document.perimetre_supervise.result.fields.indexOf(
      arrayTest[randomNumber],
    )
    if (exist == '-1') {
      res.data[0].document.perimetre_supervise.result.fields.push(
        arrayTest[randomNumber],
      )
    }
  }
  
  res.data[0].document.perimetre_supervise.result.fields.map(
    () => {
      return res.data[0].document.perimetre_supervise.result.rows.push(
        Math.floor(Math.random() * 5000).toString(),
      )
    },
  )

  return res
}

/**
 * Traite les KPIs pour un ensemble de codes de projet
 */
export const processKpis = async (promres, projectCodes) => {
  if (!projectCodes || projectCodes.length === 0) {
    return { all: promres, merge: {} }
  }

  // Récupération des données KPI pour chaque code
  await Promise.all(
    projectCodes.map(async (code) => {
      await kpiCall(code)
        .then((kpis) => {
          if (!promres.codes) {
            promres.codes = {}
          }
          if (!promres.codes[code]) {
            promres.codes[code] = {}
          }
          promres.codes[code].kpis = kpis
        })
        .catch((e) => {
          console.log(e)
        })
    }),
  )

  // Fusion des données KPI
  const mergedkpis = {}
  const fieldskpis = {}

  // Traitement spécial pour perimetre_supervise
  const psToArray = Object.entries(promres.codes)
  const perimetreSupervise = psToArray.reduce((obj, val) => {
    const { kpis } = val[1]
    const { perimetre_supervise } = kpis
    if (!perimetre_supervise || !perimetre_supervise.result) {
      return obj
    }
    const { fields, rows } = perimetre_supervise.result
    if (fields.length && rows.length) {
      // Splunk renvoie rows comme array d'arrays (chaque sous-array = 1 ligne).
      // Pour le périmètre supervisé (snapshot), on agrège toutes les lignes.
      rows.forEach((row) => {
        const values = Array.isArray(row) ? row : [row]
        fields.forEach((field, index) => {
          const v = Number(values[index])
          if (!Number.isNaN(v)) {
            obj[field] = (obj[field] || 0) + v
          }
        })
      })
    }
    return obj
  }, {})

  // Traitement de chaque type de KPI
  for (var code in promres.codes) {
    for (var typekpi in promres.codes[code].kpis) {
      processKpiType(typekpi, promres.codes[code].kpis[typekpi], mergedkpis, fieldskpis, perimetreSupervise)
    }
  }

  // Calcul des résultats finaux
  const sumres = calculateFinalResults(mergedkpis, fieldskpis)
  
  return { all: promres, merge: sumres }
}

/**
 * Traite un type de KPI spécifique
 */
const processKpiType = (typekpi, kpiData, mergedkpis, fieldskpis, perimetreSupervise) => {
  if (
    kpiData.result &&
    kpiData.result.fields &&
    kpiData.result.fields.length > 0 &&
    !fieldskpis[typekpi] &&
    typekpi != 'perimetre_supervise'
  ) {
    fieldskpis[typekpi] = kpiData.result.fields
  }

  switch (typekpi) {
    case 'er_supervise':
      processErSupervise(kpiData, mergedkpis)
      break
    case 'mitre_attack':
      processMitreAttack(kpiData, mergedkpis)
      break
    case 'perimetre_supervise':
      processPerimetreSupervise(perimetreSupervise, mergedkpis)
      break
    case 'log':
      processLog(kpiData, mergedkpis)
      break
  }
}

const processErSupervise = (kpiData, mergedkpis) => {
  if (!mergedkpis['er_supervise']) {
    mergedkpis['er_supervise'] = []
  }

  if (
    kpiData.result &&
    kpiData.result.rows &&
    typeof kpiData.result.rows[0] == 'number'
  ) {
    mergedkpis['er_supervise'].push(kpiData.result.rows)
  }
}

const processMitreAttack = (kpiData, mergedkpis) => {
  if (!mergedkpis['mitre_attack']) {
    mergedkpis['mitre_attack'] = []
  }
  
  if (
    kpiData.result &&
    kpiData.result.rows &&
    typeof kpiData.result.rows[0] == 'object'
  ) {
    for (var f in kpiData.result.fields) {
      if (!mergedkpis['mitre_attack'][f]) {
        mergedkpis['mitre_attack'][f] = []
      }
      mergedkpis['mitre_attack'][f].push(kpiData.result.rows[f])
    }
  }
}

const processPerimetreSupervise = (perimetreSupervise, mergedkpis) => {
  if (!mergedkpis['perimetre_supervise'] && perimetreSupervise) {
    mergedkpis['perimetre_supervise'] = perimetreSupervise
  }
}

const processLog = (kpiData, mergedkpis) => {
  if (!mergedkpis['log']) {
    mergedkpis['log'] = {}
  }

  if (kpiData.result && Array.isArray(kpiData.result.rows)) {
    kpiData.result.rows.forEach((row) => {
      if (!Array.isArray(row) || row.length === 0) return
      // Normalise la date à la journée (ignore HH:MM:SS) pour permettre
      // l'agrégation entre feuilles dont les saved searches ont été
      // dispatchées à des heures différentes.
      const rawDate = row[0]
      const day = typeof rawDate === 'string' ? rawDate.slice(0, 10) : rawDate
      const values = row.slice(1) // non destructif, contrairement à shift()
      if (!mergedkpis['log'][day]) {
        mergedkpis['log'][day] = []
      }
      mergedkpis['log'][day].push(values)
    })
  }
}

/**
 * Calcule les résultats finaux pour chaque type de KPI
 */
const calculateFinalResults = (mergedkpis, fieldskpis) => {
  const sumres = {}
  
  for (var typekpi in mergedkpis) {
    switch (typekpi) {
      case 'er_supervise':
        sumres[typekpi] = {
          result: {
            fields: fieldskpis[typekpi],
            rows: sumArrays(...mergedkpis[typekpi]),
          },
        }
        break
      case 'mitre_attack':
        const subres = []
        for (var f in mergedkpis[typekpi]) {
          subres[f] = sumArrays(...mergedkpis[typekpi][f])
        }
        sumres[typekpi] = {
          result: { fields: fieldskpis[typekpi], rows: subres },
        }
        break
      case 'perimetre_supervise':
        sumres[typekpi] = {
          result: {
            fields: Object.keys(mergedkpis[typekpi]),
            rows: Object.values(mergedkpis[typekpi]),
          },
        }
        break
      case 'log': {
        const logSubres = {}
        for (const d in mergedkpis[typekpi]) {
          // sumArrays additionne les valeurs (site_paris, site_lyon, …) des
          // différentes feuilles partageant la même date.
          logSubres[d] = sumArrays(...mergedkpis[typekpi][d])
        }
        // Tri chronologique pour que Chart.js (TimeScale) affiche
        // proprement les barres alignées sur l'axe X.
        const sortedDates = Object.keys(logSubres).sort()
        const rowsres = sortedDates.map((d) => [d, ...logSubres[d]])
        sumres[typekpi] = {
          result: { fields: fieldskpis[typekpi], rows: rowsres },
        }
        break
      }
    }
  }
  
  return sumres
}
